import { Component, OnInit } from '@angular/core';
import { ApplicationViewsService } from './Services/application-views.services';
import { MaintenanceService } from '../services/maintenance.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationViewsResource, ViewInfoResource } from 'src/app/client-api.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PanelSize } from 'src/app/shared/tcc-ui/simple-panel/models';

@Component({
  selector: 'app-application-views-upsert',
  templateUrl: './application-views-upsert.component.html',
  styleUrls: ['./application-views-upsert.component.scss']
})
export class ApplicationViewsUpsertComponent implements OnInit {
  
  allApplicationViews: ApplicationViewsResource[] | undefined;
  applicationViewsCategories: ViewInfoResource[] | undefined;  
  applicationViewFormGroup = new FormGroup({
    id: new FormControl(0, { nonNullable: true }),
    key: new FormControl('', { nonNullable: true, validators: Validators.required }),
    view: new FormControl('', { nonNullable: true, validators: Validators.required }),
    accountCode: new FormControl('', { nonNullable: true, validators: Validators.required }),
    parentAccountCode: new FormControl('', { nonNullable: true, validators: Validators.required }),
    name: new FormControl('', { nonNullable: true, validators: Validators.required }),
    calculationExpression: new FormControl('', { nonNullable: true }),
    displayAccountCode: new FormControl('', { nonNullable: true }),
    isNegated: new FormControl(false, { nonNullable: true, validators: Validators.required }),
    sortIndex: new FormControl(0, { nonNullable: true, validators: Validators.required }),
    isReadOnly: new FormControl(false, { nonNullable: true, validators: Validators.required }),
    isIgnoredInTotals: new FormControl(false, { nonNullable: true, validators: Validators.required }),
    customValidation: new FormControl('', { nonNullable: true }),
    isHidden: new FormControl(false, { nonNullable: true, validators: Validators.required })
  });
  
  datasetDirty = false;
  duplicateKey = false;
  addOrEdit: 'add' | 'edit';
  currentId = 0;
  currentApplicationView: ApplicationViewsResource | undefined;
  generatedKey = '';
  panalSize: PanelSize = 'large';
  state: 'initializing' | 'loading' | 'ready' | 'saving';  
  subscription: Subscription | undefined;

  constructor(private appViewsServices: ApplicationViewsService,
              private maintenanceService: MaintenanceService,
              private activatedRoute: ActivatedRoute,
              private router: Router) {
    this.state = 'initializing';  
    this.addOrEdit = 'edit';
  }

  ngOnInit(): void {    
    this.appViewsServices.setApplicationViews();
    this.subscription = this.appViewsServices.applicationViews$.subscribe(res => {
      this.allApplicationViews = res;      
    });

    this.getApplicationViewCategories();
    this.activatedRoute.params.subscribe(routeParam => {
      this.currentId =  routeParam['id'];      
      if(routeParam['id'] !== '0') {
        this.addOrEdit = 'edit';
        this.getApplicationView(this.currentId);
        this.applicationViewFormGroup.controls['view'].disable();
        this.applicationViewFormGroup.controls['accountCode'].disable();
      }
      else {
        this.addOrEdit = 'add';
        this.state = 'ready';        
        this.applicationViewFormGroup.reset();
        this.applicationViewFormGroup.controls['view'].enable();
        this.applicationViewFormGroup.controls['accountCode'].enable();
      }
    });    
  }   

  getApplicationView(id: any) {
    this.state = 'loading';
    this.appViewsServices.getApplicationView(id)
        .subscribe(
          res => {
            this.state = 'ready';
            this.currentApplicationView = res;
            this.applicationViewFormGroup.patchValue({
              accountCode: res.accountCode,
              calculationExpression: res.calculationExpression,
              customValidation: res.customValidation,
              displayAccountCode: res.displayAccountCode,
              id: res.id,
              isHidden: res.isHidden,
              isNegated: res.isNegated,
              isReadOnly: res.isReadOnly,
              isIgnoredInTotals: res.isIgnoredInTotals,
              key: res.key,              
              name: res.name,
              parentAccountCode: res.parentAccountCode,
              sortIndex: res.sortIndex,
              view: res.view
            });
            
            this.generateKey();
          },
          err => {
            this.state = 'ready';
            alert('Error getting Application View from database!');
          });
  }
  
  getApplicationViewCategories() {
    this.appViewsServices.getApplicationViewsCategories()
        .subscribe(
          res => {
            this.applicationViewsCategories = res;
          },
          err => {            
            alert('Error getting Avvount Vuew Categories!');
          }
        )
  }

  close() {
    this.maintenanceService.setMaintenanceComponenetPanelSize('full');
    this.router.navigate(['.'], { relativeTo: this.activatedRoute.parent });
  }

  fieldChanged(elem: HTMLInputElement) {    
    this.generateKey();    
    return this.applicationViewFormGroup.get(elem.name)?.dirty;
  }

  generateKey() {
    const accountCode = this.applicationViewFormGroup.get('accountCode')?.value;
    const view = this.applicationViewFormGroup.get('view')?.value;      
    this.generatedKey = view + ':' + accountCode;    

    this.checkForDuplicatedKey();
  }

  checkForDuplicatedKey() {
    const duped = this.allApplicationViews?.find(a => a.key === this.generatedKey);
    if(duped){
      this.duplicateKey = true;
    }
    else {
      this.duplicateKey = false;
    }
  }

  isChanged(field: string) {
    return this.applicationViewFormGroup.get(field)?.dirty;
  }

  isEditMode(){
    return this.addOrEdit === 'edit';
  }
  
  onSubmit() {

    if(this.addOrEdit === 'add') {
      this.applicationViewFormGroup.controls['id'].setValue(0); 
    }

    this.applicationViewFormGroup.controls['view'].enable();
    this.applicationViewFormGroup.controls['accountCode'].enable();

    let appViewRes: ApplicationViewsResource = {
      accountCode: this.applicationViewFormGroup.controls['accountCode'].value,
      id:  this.applicationViewFormGroup.controls['id'].value,
      parentAccountCode: this.applicationViewFormGroup.controls['parentAccountCode'].value,
      key: this.applicationViewFormGroup.controls['key'].value,
      calculationExpression: this.applicationViewFormGroup.controls['calculationExpression'].value,
      view: this.applicationViewFormGroup.controls['view'].value,
      name: this.applicationViewFormGroup.controls['name'].value,
      displayAccountCode: this.applicationViewFormGroup.controls['displayAccountCode'].value,
      sortIndex: this.applicationViewFormGroup.controls['sortIndex'].value,
      customValidation: this.applicationViewFormGroup.controls['customValidation'].value,
      isChecked: false,
      isHidden: this.applicationViewFormGroup.controls['isHidden'].value,
      isNegated: this.applicationViewFormGroup.controls['isNegated'].value,
      isReadOnly: this.applicationViewFormGroup.controls['isReadOnly'].value,
      isIgnoredInTotals: this.applicationViewFormGroup.controls['isIgnoredInTotals'].value,
    };

    this.applicationViewFormGroup.controls['view'].enable();
    this.applicationViewFormGroup.controls['accountCode'].enable();

      this.appViewsServices.putApplicationView(appViewRes).subscribe(
        res => {
          this.appViewsServices.setApplicationViews();
          this.close();
        },
        err => {
          alert('Error adding Application View!');
        });
  }
  
  updateViewName() {
    this.generateKey();
  }
}
